import { useState, useEffect } from "react";
import { useFlowConfig } from "@hooks/flow";
import type { FlowConfigViewModel } from "@models/doctype";
import { generateStepsForFlowType } from "@utils/flowStepGenerator";
import type {
  FlowStepFormData,
  LLMStepConfigFormData,
  OCRStepConfigFormData,
  AgentExecutionFormData,
} from "@utils/builders/flowConfig";
import type { ProviderViewModel } from "@models/provider";
import type { ModelViewModel, ModelBasicInfoViewModel } from "@models/model";

interface UseFlowWizardProps {
  open: boolean;
  existingConfig?: FlowConfigViewModel | null;
}

export const useFlowWizard = ({ open, existingConfig }: UseFlowWizardProps) => {
  const {
    fetchProviders,
    fetchModelsBasicByProviderKey,
    fetchModelDetailByName,
  } = useFlowConfig();

  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState("0");
  const [flowType, setFlowType] = useState<string>("");
  const [flowDescription, setFlowDescription] = useState<string>("");
  const [steps, setSteps] = useState<FlowStepFormData[]>([]);
  const [isFlowTypeSelected, setIsFlowTypeSelected] = useState(false);

  const [providers, setProviders] = useState<ProviderViewModel[]>([]);
  const [models, setModels] = useState<ModelBasicInfoViewModel[]>([]);
  const [selectedProvider, setSelectedProvider] =
    useState<ProviderViewModel | null>(null);
  const [selectedModel, setSelectedModel] = useState<ModelViewModel | null>(
    null,
  );
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);

  // Agent models state
  const [llmProviders, setLlmProviders] = useState<ProviderViewModel[]>([]);
  const [llmModels, setLlmModels] = useState<
    Record<string, ModelBasicInfoViewModel[]>
  >({});

  // Generate steps based on flow type. Advancing past the type selector is
  // driven by the Continue button (setIsFlowTypeSelected), not by selection.
  useEffect(() => {
    if (open && !existingConfig && flowType) {
      setSteps(generateStepsForFlowType(flowType));
    }
  }, [flowType, open, existingConfig]);

  useEffect(() => {
    if (existingConfig && open) {
      setFlowType(existingConfig.flowType);
      setFlowDescription(existingConfig.flowDescription || "");
      setIsFlowTypeSelected(true);
      setSteps(
        existingConfig.steps.map((step) => ({
          stepOrder: step.stepOrder,
          stepType: step.stepType,
          llmStepConfig: step.llmStepConfig
            ? {
                provider: step.llmStepConfig.provider,
                systemPrompt: step.llmStepConfig.systemPrompt,
                userPrompt: step.llmStepConfig.userPrompt,
                modelName: step.llmStepConfig.modelName,
                configOptions: step.llmStepConfig.configOptions,
                responseSchema: step.llmStepConfig.responseSchema,
                executions:
                  step.llmStepConfig.executions?.map((a) => ({
                    domainKey: a.domainKey,
                    userPrompt: a.userPrompt,
                    responseSchema: a.responseSchema,
                    responseSchemaText: JSON.stringify(a.responseSchema, null, 2),
                    systemPrompt: a.systemPrompt || "",
                    modelName: a.modelName || "",
                    configOptions: a.configOptions,
                  })) || null,
              }
            : undefined,
          ocrStepConfig: step.ocrStepConfig
            ? {
                provider: step.ocrStepConfig.provider,
                modelName: step.ocrStepConfig.modelName,
                ocrOptions: step.ocrStepConfig.ocrOptions,
              }
            : undefined,
        })),
      );
    }
  }, [existingConfig, open]);

  // Load providers when step changes
  useEffect(() => {
    if (
      open &&
      steps.length > 0 &&
      activeStep < steps.length &&
      isFlowTypeSelected
    ) {
      loadProvidersForCurrentStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, open, isFlowTypeSelected]);

  const loadProvidersForCurrentStep = async () => {
    const currentStep = steps[activeStep];
    if (!currentStep) return;

    setIsLoadingProviders(true);
    const providerType = currentStep.ocrStepConfig ? "ocr" : "llm";
    const config = currentStep.llmStepConfig || currentStep.ocrStepConfig;

    try {
      const [providersList] = await Promise.all([fetchProviders(providerType)]);
      setProviders(providersList);

      // Also load LLM providers for agent model selection
      if (Array.isArray(currentStep.llmStepConfig?.executions)) {
        const llmList =
          providerType === "llm" ? providersList : await fetchProviders("llm");
        setLlmProviders(llmList);
      }

      // Auto-select provider and load models if exists
      if (config && config.provider) {
        const provider = providersList.find((p) => p.key === config.provider);
        if (provider) {
          setSelectedProvider(provider);

          const [modelsList, modelDetail] = await Promise.all([
            fetchModelsBasicByProviderKey(config.provider),
            config.modelName
              ? fetchModelDetailByName(config.modelName)
              : Promise.resolve(null),
          ]);

          setModels(modelsList);
          if (modelDetail) {
            setSelectedModel(modelDetail);
          }
        }
      }
    } finally {
      setIsLoadingProviders(false);
    }
  };

  const loadModels = async (
    providerKey: string,
    modelNameToSelect?: string,
  ) => {
    setIsLoadingModels(true);

    const promises: [
      Promise<ModelBasicInfoViewModel[]>,
      Promise<ModelViewModel | null>,
    ] = [
      fetchModelsBasicByProviderKey(providerKey),
      modelNameToSelect
        ? fetchModelDetailByName(modelNameToSelect)
        : Promise.resolve(null),
    ];

    const [modelsList, modelDetail] = await Promise.all(promises);

    setModels(modelsList);
    if (modelDetail) {
      setSelectedModel(modelDetail);
    }

    setIsLoadingModels(false);
  };

  const handleProviderChange = async (providerId: string) => {
    const provider = providers.find((p) => p.id === providerId);
    if (!provider) return;

    setSelectedProvider(provider);
    setSelectedModel(null);
    setModels([]);

    updateCurrentStepConfig("provider", provider.key);
    updateCurrentStepConfig("modelName", "");

    await loadModels(provider.key);
  };

  const handleModelChange = async (modelName: string) => {
    updateCurrentStepConfig("modelName", modelName);

    const modelDetail = await fetchModelDetailByName(modelName);
    if (modelDetail) {
      setSelectedModel(modelDetail);
    }
  };

  const loadAgentModels = async (
    providerKey: string,
    _providerType: "ocr" | "llm",
  ) => {
    const models = await fetchModelsBasicByProviderKey(providerKey);
    setLlmModels((prev) => ({ ...prev, [providerKey]: models }));
  };

  const updateAgents = (agents: AgentExecutionFormData[]) => {
    const updatedSteps = [...steps];
    const currentStep = updatedSteps[activeStep];
    if (currentStep.llmStepConfig) {
      currentStep.llmStepConfig = {
        ...currentStep.llmStepConfig,
        executions: agents,
      };
      setSteps(updatedSteps);
    }
  };

  const updateCurrentStepConfig = (
    field: string,
    value: string | Record<string, unknown> | null,
  ) => {
    const updatedSteps = [...steps];
    const currentStep = updatedSteps[activeStep];

    if (currentStep.llmStepConfig) {
      currentStep.llmStepConfig = {
        ...currentStep.llmStepConfig,
        [field]: value,
      } as LLMStepConfigFormData;
    } else if (currentStep.ocrStepConfig) {
      currentStep.ocrStepConfig = {
        ...currentStep.ocrStepConfig,
        [field]: value,
      } as OCRStepConfigFormData;
    }

    setSteps(updatedSteps);
  };

  const isStepValid = (stepIndex: number): boolean => {
    const step = steps[stepIndex];
    if (!step) return false;

    if (step.llmStepConfig) {
      const hasBase = !!(
        step.llmStepConfig.provider &&
        step.llmStepConfig.modelName &&
        step.llmStepConfig.systemPrompt &&
        step.llmStepConfig.userPrompt
      );
      if (!hasBase) return false;

      // If agents exist, validate them
      if (step.llmStepConfig.executions?.length) {
        return step.llmStepConfig.executions.every(
          (a) => a.domainKey && a.userPrompt && a.responseSchema,
        );
      }
      return true;
    }

    if (step.ocrStepConfig) {
      return !!(step.ocrStepConfig.provider && step.ocrStepConfig.modelName);
    }

    return false;
  };

  const canProceed = (): boolean => {
    return isStepValid(activeStep);
  };

  const canSubmit = (): boolean => {
    return steps.every((_, index) => isStepValid(index));
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
      setActiveTab("0");
      setSelectedProvider(null);
      setSelectedModel(null);
      setModels([]);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setActiveTab("0");
      setSelectedProvider(null);
      setSelectedModel(null);
      setModels([]);
    }
  };

  return {
    activeStep,
    activeTab,
    flowType,
    flowDescription,
    steps,
    isFlowTypeSelected,
    providers,
    models,
    selectedProvider,
    selectedModel,
    isLoadingModels,
    isLoadingProviders,
    llmProviders,
    llmModels,
    setActiveTab,
    setFlowType,
    setFlowDescription,
    setIsFlowTypeSelected,
    handleProviderChange,
    handleModelChange,
    updateCurrentStepConfig,
    isStepValid,
    canProceed,
    canSubmit,
    handleNext,
    handleBack,
    loadAgentModels,
    updateAgents,
  };
};
